import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import { DOC_SECTIONS } from '../../utils/docsContent';

// ─── Content block renderer ──────────────────────────────────────────────────

const DocBlock = ({ block }) => {
  switch (block.type) {
    case 'paragraph':
      return <p className="text-[#4A5568] leading-relaxed mb-3">{block.text}</p>;

    case 'subheading':
      return <h4 className="font-semibold text-[#1A2E6E] mt-4 mb-2">{block.text}</h4>;

    case 'note':
      return (
        <div className="bg-blue-50 border-l-4 border-blue-400 rounded px-4 py-2 mb-3 text-sm text-blue-800">
          {block.text}
        </div>
      );

    case 'list':
      return (
        <ul className="list-disc list-inside space-y-1 mb-3 text-[#4A5568]">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed">{item}</li>
          ))}
        </ul>
      );

    case 'table':
      return (
        <div className="overflow-x-auto mb-3">
          <table className="w-full text-sm border border-[#E8EAF0] rounded-lg overflow-hidden">
            <thead className="bg-[#F0F2FA]">
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} className="px-4 py-2 text-left font-semibold text-[#1A2E6E] border-b border-[#E8EAF0]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FC]'}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-2 text-[#4A5568] border-b border-[#E8EAF0]">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'faq':
      return (
        <div className="space-y-3 mb-3">
          {block.items.map((item, i) => (
            <div key={i} className="border border-[#E8EAF0] rounded-lg p-4">
              <p className="font-semibold text-[#1A2E6E] mb-1">Q: {item.q}</p>
              <p className="text-[#4A5568] text-sm leading-relaxed">A: {item.a}</p>
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

// ─── Section card ────────────────────────────────────────────────────────────

const SectionCard = ({ section, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      id={section.id}
      className="bg-white border border-[#E8EAF0] rounded-xl overflow-hidden mb-3"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[#F8F9FC] transition-colors"
      >
        <span className="font-semibold text-[#1A2E6E]">{section.title}</span>
        {open ? (
          <ChevronDown size={18} className="text-[#1A2E6E]/50 flex-shrink-0" />
        ) : (
          <ChevronRight size={18} className="text-[#1A2E6E]/50 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-5 pt-1 border-t border-[#E8EAF0]">
          {section.content.map((block, i) => (
            <DocBlock key={i} block={block} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Sidebar nav group ───────────────────────────────────────────────────────

const GROUPS = [
  { label: 'Administrator', audiences: ['admin'], ids: [
    'first-login', 'dashboard', 'leads', 'managing-students', 'managing-teachers',
    'groups', 'classes-scheduling', 'activities-grading', 'plans', 'billing',
    'coupons', 'messages', 'chat', 'availability', 'media', 'cms',
    'testimonials-admin', 'level-test-admin', 'settings', 'smtp',
  ]},
  { label: 'Teacher', audiences: ['teacher'], ids: [
    'dashboard', 'leads', 'managing-students', 'groups', 'classes-scheduling',
    'activities-grading', 'messages', 'chat',
  ]},
  { label: 'Student', audiences: ['student'], ids: [
    'student-registration', 'student-onboarding', 'student-dashboard',
    'student-billing', 'student-schedule', 'student-classes', 'student-available',
    'student-activities', 'student-messages', 'student-chat', 'student-profile',
  ]},
  { label: 'Parents & Visitors', audiences: ['parent'], ids: [
    'public-website', 'level-test', 'contact-form', 'book-trial',
  ]},
  { label: 'FAQ', audiences: ['admin', 'teacher', 'student', 'parent'], ids: ['faq'] },
];

// ─── Main page ───────────────────────────────────────────────────────────────

export const AdminDocumentation = () => {
  const [search, setSearch] = useState('');
  const [activeGroup, setActiveGroup] = useState('Administrator');
  const sectionRefs = useRef({});

  const sectionMap = useMemo(() => {
    const map = {};
    DOC_SECTIONS.forEach((s) => { map[s.id] = s; });
    return map;
  }, []);

  // When searching, flatten all sections and filter by title + content text
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return DOC_SECTIONS.filter((s) => {
      if (s.title.toLowerCase().includes(q)) return true;
      return s.content.some((block) => {
        if (block.text && block.text.toLowerCase().includes(q)) return true;
        if (block.items && block.items.some((it) => it.toLowerCase().includes(q))) return true;
        if (block.rows && block.rows.some((r) => r.some((c) => c.toLowerCase().includes(q)))) return true;
        if (block.items && block.items.some((it) => (it.q + it.a).toLowerCase().includes(q))) return true;
        return false;
      });
    });
  }, [search]);

  const activeGroupData = GROUPS.find((g) => g.label === activeGroup);
  const activeSections = activeGroupData
    ? activeGroupData.ids.map((id) => sectionMap[id]).filter(Boolean)
    : [];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <BookOpen size={24} className="text-[#D72B2B]" />
          <h1 className="text-2xl font-bold text-[#1A2E6E]">Documentation</h1>
        </div>
        <p className="text-[#4A5568]">User guides and reference for all roles</p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40" />
        <input
          type="text"
          placeholder="Search documentation..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#E8EAF0] bg-white focus:outline-none focus:ring-2 focus:ring-[#1A2E6E]/20 text-[#1A2E6E] placeholder:text-[#1A2E6E]/40"
        />
      </div>

      {searchResults ? (
        /* Search results */
        <div>
          <p className="text-sm text-[#4A5568] mb-4">
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{search}"
          </p>
          {searchResults.length === 0 ? (
            <div className="text-center py-12 text-[#4A5568]/60">
              No sections match your search.
            </div>
          ) : (
            searchResults.map((section) => (
              <SectionCard key={section.id} section={section} defaultOpen />
            ))
          )}
        </div>
      ) : (
        /* Normal view with sidebar + content */
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-52 flex-shrink-0 hidden lg:block">
            <div className="bg-white border border-[#E8EAF0] rounded-xl p-3 sticky top-4">
              {GROUPS.map((group) => (
                <div key={group.label}>
                  <button
                    onClick={() => setActiveGroup(group.label)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold transition-colors mb-1 ${
                      activeGroup === group.label
                        ? 'bg-[#1A2E6E] text-white'
                        : 'text-[#1A2E6E] hover:bg-[#F0F2FA]'
                    }`}
                  >
                    {group.label}
                  </button>
                  {activeGroup === group.label && (
                    <ul className="ml-3 mb-2 space-y-0.5">
                      {group.ids.map((id) => {
                        const sec = sectionMap[id];
                        if (!sec) return null;
                        return (
                          <li key={id}>
                            <button
                              onClick={() => scrollTo(id)}
                              className="w-full text-left px-2 py-1 rounded text-xs text-[#4A5568] hover:text-[#1A2E6E] hover:bg-[#F0F2FA] transition-colors"
                            >
                              {sec.title}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </aside>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            {/* Mobile group tabs */}
            <div className="flex gap-2 flex-wrap mb-4 lg:hidden">
              {GROUPS.map((g) => (
                <button
                  key={g.label}
                  onClick={() => setActiveGroup(g.label)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    activeGroup === g.label
                      ? 'bg-[#1A2E6E] text-white'
                      : 'bg-white border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#F0F2FA]'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>

            <h2 className="text-lg font-bold text-[#1A2E6E] mb-4">{activeGroup}</h2>

            {activeSections.map((section) => (
              <SectionCard key={section.id} section={section} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDocumentation;
